import { MerchantApiError } from "../authentication/errors.js";
import type { MerchantApiClient } from "../../types.js";
import { dispatchMerchantTelemetry } from "../../shared/telemetry.js";
import { createMonitoringApi, type MonitoringApi } from "./api.js";
import type {
  MonitoringFilters,
  MonitoringObservationPage,
  MonitoringSettingsDraft,
  MonitoringSummary,
  MonitoringRunNowResult,
  NotificationPreferenceRecord,
} from "./models.js";

export interface MonitoringTelemetry {
  track(eventName: string, payload: Record<string, string | number | boolean | undefined>): void;
}

export interface MonitoringService {
  loadNotificationPreferences(): Promise<NotificationPreferenceRecord>;
  loadObservationHistory(
    mappingId: string,
    productId: string,
    month: string,
    limit: number,
    cursor?: string,
  ): Promise<MonitoringObservationPage>;
  loadSummary(filters?: MonitoringFilters): Promise<MonitoringSummary>;
  runNow(mappingId: string, productId: string): Promise<MonitoringRunNowResult>;
  saveNotificationPreferences(
    preferences: NotificationPreferenceRecord,
  ): Promise<NotificationPreferenceRecord>;
  saveSettings(
    draft: MonitoringSettingsDraft,
  ): Promise<MonitoringSummary | MonitoringSummary["mappings"][number]>;
}

export interface MonitoringServiceOptions {
  api?: MonitoringApi;
  apiClient?: MerchantApiClient;
  idempotencyKeyFactory?: () => string;
  telemetry?: MonitoringTelemetry;
}

export function createMonitoringService(options: MonitoringServiceOptions): MonitoringService {
  const api = options.api ?? createApiFromClient(options.apiClient);
  const telemetry = options.telemetry ?? browserTelemetry();
  const idempotencyKeyFactory = options.idempotencyKeyFactory ?? (() => crypto.randomUUID());

  const pendingChecks = new Map<string, { key: string; uncertain: boolean }>();
  async function runWithOriginalKey(
    mappingId: string,
    productId: string,
  ): Promise<MonitoringRunNowResult> {
    const target = JSON.stringify([productId, mappingId]);
    const pending = pendingChecks.get(target) ?? { key: idempotencyKeyFactory(), uncertain: false };
    pendingChecks.set(target, pending);
    try {
      const result = await api.runNow(mappingId, productId, pending.key);
      pendingChecks.delete(target);
      return result;
    } catch (error: unknown) {
      const definite =
        error instanceof MerchantApiError &&
        !error.retryable &&
        !["network", "server", "unknown"].includes(error.kind);
      if (definite && !pending.uncertain) pendingChecks.delete(target);
      else pending.uncertain = true;
      throw error;
    }
  }

  return {
    loadNotificationPreferences() {
      return track(
        telemetry,
        "merchant_monitoring_notification_preferences_loaded",
        () => api.getNotificationPreferences(),
        {},
      );
    },
    loadObservationHistory(mappingId, productId, month, limit, cursor) {
      return track(
        telemetry,
        "merchant_monitoring_observations_loaded",
        () => api.getObservationHistory(mappingId, productId, month, limit, cursor),
        { mappingId, month },
      );
    },
    loadSummary(filters) {
      return track(telemetry, "merchant_monitoring_summary_loaded", () => api.getSummary(filters), {
        health: filters?.health,
        sort: filters?.sort,
      });
    },
    runNow(mappingId, productId) {
      return track(
        telemetry,
        "merchant_monitoring_run_now",
        () => runWithOriginalKey(mappingId, productId),
        { mappingId, productId },
      );
    },
    saveNotificationPreferences(preferences) {
      return track(
        telemetry,
        "merchant_monitoring_alert_preferences_saved",
        () => api.updateNotificationPreferences(preferences, idempotencyKeyFactory()),
        {},
      );
    },
    saveSettings(draft) {
      return track(
        telemetry,
        "merchant_monitoring_settings_saved",
        () => api.updateSettings(draft, idempotencyKeyFactory()),
        { mappingId: draft.mappingId, enabled: draft.enabled, cadence: draft.cadence },
      );
    },
  };
}

async function track<T>(
  telemetry: MonitoringTelemetry,
  eventName: string,
  operation: () => Promise<T>,
  payload: Record<string, string | number | boolean | undefined>,
): Promise<T> {
  const startedAt = performance.now();
  try {
    const result = await operation();
    telemetry.track(eventName, {
      ...payload,
      result: "success",
      durationMs: Math.round(performance.now() - startedAt),
    });
    return result;
  } catch (error: unknown) {
    telemetry.track(eventName, {
      ...payload,
      result: "failure",
      durationMs: Math.round(performance.now() - startedAt),
      errorName: error instanceof Error ? error.name : "unknown",
    });
    throw error;
  }
}

function createApiFromClient(client: MerchantApiClient | undefined): MonitoringApi {
  if (client === undefined) {
    throw new Error("Monitoring service requires an API client.");
  }

  return createMonitoringApi(client);
}

function browserTelemetry(): MonitoringTelemetry {
  return {
    track(eventName, payload) {
      dispatchMerchantTelemetry(eventName, payload);
    },
  };
}
